import { useEffect, useState } from 'react'
import { Modal, Button, LoadingIcons, Fa } from '@eltoro-ui/components'
import {
  Campaignservicev1OrderLine,
  Creativeservicev1Creative,
  V1Results,
  V1StatsResponse,
} from 'next-gen-sdk'
import { StatsAccumulatorType } from 'types'
import { dayjs } from 'Tools/dateUtils'
import { styleTailwind } from 'Helpers'
import { useAppContext } from 'Contexts'
import { getOrderLineStats } from 'Helpers/getOrderLineStats'
import { Stats } from './Stats'
import { CSVColumnPickerModal } from './DownloadCSVModal'
import { downloadPDFReport } from './downloadPDFReport'
import { testCreativeStats } from './testCreativesStats'
import './ReportModal.scss'

const ActionButton = styleTailwind(
  'span',
  'ActionButton group px-4 flex h-8 w-44 focus:ring transition-all items-center justify-center',
)

export type DailyBreakdownType = 'clicks' | 'impressions' | 'ctr'
type ReportModalType = {
  orderline: Campaignservicev1OrderLine
  closeModal: () => void
}
export const ReportModal = ({ orderline, closeModal }: ReportModalType) => {
  const [currentlyDownloading, setCurrentlyDownloading] = useState<
    'pdf' | 'csv'
  >()
  const [week, setWeek] = useState(0)
  const [item, setItem] = useState<DailyBreakdownType>('clicks')
  const [olStatsData, setOlStatsData] = useState<V1StatsResponse>()
  const [creativeStatsData, setCreativeStatsData] = useState<V1StatsResponse>()
  const [sortedCreativeStats, setSortedCreativeStats] = useState<
    StatsAccumulatorType[]
  >([])
  const [columnModal, setColumnModal] = useState(false)
  const [brandingModal, setBrandingModal] = useState(false)
  const [creatives, setCreatives] = useState<Creativeservicev1Creative[]>([])
  const {
    statsServiceApi,
    creativeServiceApi,
    currentOrg,
    orgServiceApi,
    timeZone,
  } = useAppContext()

  // set this to true with ol to test multiple detached creatives that are returned in the stats, but are not on the OL:
  const testDetachedCreativeFlag = false

  // Determine if user has access to Billable org
  const [hasAccessToBillableOrg, setHasAccessToBillableOrg] = useState<boolean>(
    false,
  )
  const [billableOrg, setBillableOrg] = useState<any>(null)

  useEffect(() => {
    if (currentOrg?.billableOrgId && orgServiceApi) {
      orgServiceApi
        .advertisingPlatformServiceGetOrg(currentOrg.billableOrgId)
        .then((org) => {
          setHasAccessToBillableOrg(true)
          setBillableOrg(org)
        })
        .catch(() => {
          setHasAccessToBillableOrg(false)
          setBillableOrg(null)
        })
    } else {
      setHasAccessToBillableOrg(false)
      setBillableOrg(null)
    }
  }, [currentOrg?.billableOrgId, orgServiceApi])

  useEffect(() => {
    const getOLStats = async () => {
      if (
        !statsServiceApi ||
        olStatsData?.details?.ids?.[0]?.orderLineId === orderline.id
      ) {
        return
      }
      try {
        if (!orderline || !orderline.orgId) return
        // Fetch OL stats
        const olStatsRes = await getOrderLineStats(
          orderline,
          orderline.orgId,
          'day',
          timeZone,
        )

        setOlStatsData(olStatsRes)
      } catch (error) {
        console.error('Error fetching stats:', error)
      }
    }

    const getCreativeStats = async () => {
      try {
        const start = new Date(orderline?.startTime || 0)
        const end = new Date(orderline?.endTime || 0)
        if (!orderline || !orderline.orgId) return

        // Fetch creative stats
        const creativeStatsRes = await statsServiceApi?.advertisingPlatformServiceGetStats(
          orderline.orgId,
          {
            startTime: dayjs.tz(start).startOf('day').utc().toDate(),
            endTime: dayjs.tz(end).endOf('day').add(1, 'day').utc().toDate(),
            timeFrame: 'total',
            levelOfDetail: ['creative_id'],
            timezone:
              timeZone && timeZone !== 'AST' && timeZone !== 'ADT'
                ? timeZone
                : 'UTC',
            orderLineId: [orderline?.id || ' '],
          },
        )

        if (testDetachedCreativeFlag) {
          setCreativeStatsData(testCreativeStats as V1StatsResponse)
        } else {
          if (creativeStatsRes !== undefined) {
            setCreativeStatsData(creativeStatsRes)
          }
        }

        // get sorted creative stats
        const creativeStatsSorted = (testDetachedCreativeFlag
          ? (testCreativeStats?.results as V1Results[])
          : creativeStatsRes?.results
        )
          ?.reduce((acc: StatsAccumulatorType[], val: V1Results) => {
            if (!val.results) return []
            const imps = Number(val?.results[0]?.imps)
            const clicks = Number(val?.results[0]?.clicks)
            const ctr = imps && clicks ? Number((clicks / imps) * 100) : 0
            return [
              ...acc,
              {
                creativeId: val?.details?.ids?.creativeId || '',
                clicks: val?.results[0]?.clicks || 0,
                imps: val?.results[0]?.imps || 0,
                ctr: ctr || 0,
              },
            ]
          }, [])
          .sort((a, b) => {
            if (b.ctr !== undefined && a.ctr !== undefined) {
              return b.ctr - a.ctr
            }
            return 0
          })
        if (creativeStatsSorted) {
          setSortedCreativeStats(creativeStatsSorted)

          // get the creatives
          // get all ids to fetch, uniqued
          // gotta fetch em all
          const creativeIds = creativeStatsSorted.map((c) => c.creativeId)
          const uniqueIds = [...new Set(creativeIds)]
          const filter = uniqueIds.map((id) => `id="${id}"`).join(' OR ')
          let nextPageToken = ''
          const fetchedCreatives: Creativeservicev1Creative[] = []
          do {
            const res = await creativeServiceApi?.advertisingPlatformServiceListCreatives(
              300, // pageSize
              nextPageToken, // pageToken
              undefined, // orderBy
              filter,
            )
            if (res) {
              fetchedCreatives.push(...(res.creatives || []))
              nextPageToken = res.nextPageToken || ''
            } else {
              nextPageToken = ''
            }
          } while (nextPageToken !== '')

          setCreatives(fetchedCreatives)
        }
      } catch (error) {
        console.error('Error fetching creative stats:', error)
      }
    }

    getOLStats().then(() => getCreativeStats())
  }, [orderline])

  const HighestPerformingCreative = () => {
    if (sortedCreativeStats) {
      const attachCreativeStatSorted = sortedCreativeStats.filter(
        (sortedCreative) => {
          return creatives.some(
            (attachedCreative) =>
              sortedCreative.creativeId === attachedCreative?.id,
          )
        },
      )
      const highestCTRId = attachCreativeStatSorted[0]?.creativeId
      const highestCTRCreative = creatives?.filter((currentCreative) => {
        return currentCreative?.id === highestCTRId
      })
      return highestCTRCreative
    }
    return []
  }

  return (
    <>
      <div className="ReportModal__container relative">
        <Modal
          classNameContent="ReportModal ReportModal__modal-content relative"
          classNameWrap="ReportModal__modal-wrap"
          className="ReportModal__modal-container"
          offClick={() => closeModal()}
        >
          <div className="ReportModal__content-wrap--online relative z-50">
            <div className="ReportModal__headerOptions relative top-0 right-0 z-50 flex items-center justify-end gap-5">
              <Button
                className="DownloadButton"
                disabled={currentlyDownloading === 'pdf'}
                onClick={() => setBrandingModal(true)}
              >
                <ActionButton className="ReportModal__Loading-container">
                  {currentlyDownloading === 'pdf' ? (
                    <LoadingIcons />
                  ) : (
                    <>
                      <Fa
                        className="ReportModal__loading-icon"
                        icon="file-pdf"
                        size={1}
                      />
                      <span className="ReportModal__loading-label">
                        Download PDF
                      </span>
                    </>
                  )}
                </ActionButton>
              </Button>
              <Button
                className="DownloadButton"
                disabled={currentlyDownloading === 'csv'}
                onClick={() => {
                  setCurrentlyDownloading('csv')
                  setColumnModal(true)
                }}
              >
                <ActionButton className="ReportModal__Loading-container">
                  {currentlyDownloading === 'csv' ? (
                    <LoadingIcons />
                  ) : (
                    <>
                      <Fa
                        className="ReportModal__loading-icon"
                        icon="file-csv"
                        size={1}
                      />
                      <span className="ReportModal__loading-label">
                        Download CSV
                      </span>
                    </>
                  )}
                </ActionButton>
              </Button>

              <Button
                className="ReportModal__closeContainer flex h-8 w-8 pl-0 pr-0"
                type="button"
                iconOnly={<Fa icon="times" size={1} />}
                onClick={() => closeModal()}
              />
            </div>

            {creativeStatsData && (
              <Stats
                className="ReportModal__report--online"
                olStatsData={olStatsData}
                sortedCreativeStats={sortedCreativeStats}
                orderline={orderline}
                creatives={creatives}
                week={week}
                setWeek={setWeek}
                item={item}
                setItem={setItem}
                highestPerformingCreative={HighestPerformingCreative()?.[0]}
              />
            )}
          </div>
        </Modal>
        {columnModal && olStatsData && (
          <CSVColumnPickerModal
            oL={orderline}
            olStats={olStatsData}
            closeModal={() => {
              setColumnModal(false)
              setCurrentlyDownloading(undefined)
            }}
          />
        )}
        {brandingModal && (
          <Modal
            className="BrandingModal__modal-container"
            classNameContent="BrandingModal relative"
            offClick={() => setBrandingModal(false)}
          >
            <div className="BrandingModal__content">
              <h2 className="mb-4 text-xl font-semibold">
                Select PDF Branding
              </h2>
              <p className="text-tint-gray-800 mb-6">
                Choose which branding you would like for your PDF report:
              </p>
              <div className="flex flex-col gap-6">
                <button
                  className="border-grey-300 aspect-square w-full transform justify-start rounded-lg border p-6 transition-all duration-300 ease-in-out hover:scale-105 hover:border-blue-600 hover:bg-blue-100 hover:shadow-lg"
                  disabled={currentOrg?.logo === null}
                  onClick={() => {
                    setBrandingModal(false)
                    if (!currentlyDownloading) {
                      setCurrentlyDownloading('pdf')
                      if (!olStatsData) return
                      downloadPDFReport(
                        orderline,
                        olStatsData,
                        item,
                        olStatsData.results?.[0].results?.slice(week, week + 7),
                        HighestPerformingCreative()?.[0],
                        sortedCreativeStats,
                        creatives,
                        currentOrg,
                      ).then(() => setCurrentlyDownloading(undefined))
                    }
                  }}
                >
                  <div className="flex items-center gap-4">
                    {currentOrg?.logo?.key ? (
                      <img
                        src={`https://s3.amazonaws.com/c.eltoro.com/${currentOrg.logo.key}`}
                        alt="Organization Logo"
                        className="h-6 w-6 object-contain"
                      />
                    ) : (
                      <Fa icon="building" size={1.5} />
                    )}
                    <div className="text-left">
                      <div className="text-lg font-semibold">
                        Organization Branded
                      </div>
                      <div className="text-tint-gray-800 text-sm">
                        {currentOrg?.logo === null
                          ? 'Add a logo to this organization to use this feature'
                          : "Use your organization's logo and branding"}
                      </div>
                    </div>
                  </div>
                </button>
                {hasAccessToBillableOrg && (
                  <button
                    className="border-grey-300 aspect-square w-full transform justify-start rounded-lg border p-6 transition-all duration-300 ease-in-out hover:scale-105 hover:border-blue-600 hover:bg-blue-100 hover:shadow-lg"
                    disabled={billableOrg?.logo === null}
                    onClick={() => {
                      setBrandingModal(false)
                      if (!currentlyDownloading) {
                        setCurrentlyDownloading('pdf')
                        if (!olStatsData) return
                        downloadPDFReport(
                          orderline,
                          olStatsData,
                          item,
                          olStatsData.results?.[0].results?.slice(
                            week,
                            week + 7,
                          ),
                          HighestPerformingCreative()?.[0],
                          sortedCreativeStats,
                          creatives,
                          billableOrg,
                        ).then(() => setCurrentlyDownloading(undefined))
                      }
                    }}
                  >
                    <div className="flex items-center gap-4">
                      {billableOrg?.logo?.key ? (
                        <img
                          src={`https://s3.amazonaws.com/c.eltoro.com/${billableOrg.logo.key}`}
                          alt="Billable Organization Logo"
                          className="h-6 w-6 object-contain"
                        />
                      ) : (
                        <Fa icon="business-time" size={1.5} />
                      )}
                      <div className="text-left">
                        <div className="text-lg font-semibold">
                          Billable Organization Branded
                        </div>
                        <div className="text-tint-gray-800 text-sm">
                          {billableOrg?.logo === null
                            ? 'Add a logo to the billable org to use this feature'
                            : "Use the billable organization's logo and branding"}
                        </div>
                      </div>
                    </div>
                  </button>
                )}
                <button
                  className="border-grey-300 aspect-square w-full transform justify-start rounded-lg border p-6 transition-all duration-300 ease-in-out hover:scale-105 hover:border-blue-600 hover:bg-blue-100 hover:shadow-lg"
                  onClick={() => {
                    setBrandingModal(false)
                    if (!currentlyDownloading) {
                      setCurrentlyDownloading('pdf')
                      if (!olStatsData) return
                      downloadPDFReport(
                        orderline,
                        olStatsData,
                        item,
                        olStatsData.results?.[0].results?.slice(week, week + 7),
                        HighestPerformingCreative()?.[0],
                        sortedCreativeStats,
                        creatives,
                        currentOrg,
                        'eltoro',
                      ).then(() => setCurrentlyDownloading(undefined))
                    }
                  }}
                >
                  <div className="flex items-center gap-4">
                    <img
                      src={require('Assets/logo_100.png')}
                      alt="ElToro Logo"
                      className="h-6 w-6 object-contain"
                    />
                    <div className="text-left">
                      <div className="text-lg font-semibold">
                        ElToro Branded
                      </div>
                      <div className="text-tint-gray-800 text-sm">
                        Use ElToro's logo and branding
                      </div>
                    </div>
                  </div>
                </button>
              </div>
              <div className="mt-6 flex justify-end">
                <Button onClick={() => setBrandingModal(false)}>Cancel</Button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </>
  )
}
